/**
 * **Note:** Make sure to keep these constants up-to-date with the back-end.
 */
export const WEEK_STARTS_ON = {
	MONDAY: 0,
	SUNDAY: 1,
	SATURDAY: 2,
} as const;

export const SENTRY = {
	DSN: 'https://3d9b31c2dca143828ca2357021baca22@o1090531.ingest.sentry.io/4504474886012928',
	ORG: 'scoro-software-fe',
	PROJECT: 'scoro-global',
};

/**
 * All setting constants ported from `@scoro/constants` PHP package `Setting.php`.
 * Please keep these files synced if making changes.
 */
export const Setting = {
	ADAPTER_SETTINGS_QUICKBOOKS: 'adapter_settings_quickbooks',
	ADMIN_STEP_PENDING: 'adminStepPending',

	// This setting was added to sites that were using Premium package and had used advanced resources feature, so they
	// wouldn't lose that functionality when we remove the feature from Premium (https://landmark.scoro.ee/tasks/view/472589)
	ADVANCED_RESOURCES_HACK: 'advancedResourcesHack',

	ALLOW_BILLING_ACTIVITIES_WITHOUT_PRODUCT:
		'allow_billing_activities_without_product',
	AUTO_SEND_INVOICE_REMINDERS: 'auto_send_invoice_reminders',
	AXAPTA_SALESMAN: 'axapta_salesman',
	BASE_CURRENCY: 'next_currency',
	CALCULATE_AMOUNT_FROM_INVOICE_DATES: 'calculdate_amount_from_invoice_dates',
	CALCULATE_AMOUNT_FROM_ORDER_DATES: 'calculate_amount_from_order_dates',
	CALCULATE_AMOUNT_FROM_QUOTE_DATES: 'calculate_amount_from_quote_dates',
	CALENDAR_REVISION: 'calendarRevision',
	CAN_CHANGE_AMOUNTS_FOR_INVOICES_WITH_DATES:
		'can_change_amounts_for_invoices_with_dates',
	CAN_CHANGE_AMOUNTS_FOR_ORDERS_WITH_DATES:
		'can_change_amounts_for_orders_with_dates',
	CAN_CHANGE_AMOUNTS_FOR_QUOTES_WITH_DATES:
		'can_change_amounts_for_quotes_with_dates',
	CITY24_INFO_ADDON: 'city24_info_addon',
	CITY24_USERNAME: 'city24_username',
	CITY24_OVERALL_INFO_ADDON: 'city24_overall_info_addon',
	CLIENTS_TO_DIRECTO: 'clientsToDirecto',
	COMPANIES_PERSONS_TOGETHER: 'companies_persons_together',
	COMPANY_ACCOUNT_CITY: 'company_account_city',
	COMPANY_ACCOUNT_COUNTRY: 'company_account_country',
	COMPANY_ACCOUNT_COUNTY: 'company_account_county',
	COMPANY_ACCOUNT_EMAIL: 'company_account_email',
	COMPANY_ACCOUNT_EMAIL_SMTP: 'company_account_email_smtp',
	COMPANY_ACCOUNT_FAX: 'company_account_fax',
	COMPANY_ACCOUNT_FRIENDLYNAME: 'company_account_friendlyname',
	COMPANY_ACCOUNT_NAME: 'company_account_name',
	COMPANY_ACCOUNT_PHONE: 'company_account_phone',
	COMPANY_ACCOUNT_REG_NO: 'company_account_regno',
	COMPANY_ACCOUNT_STREET: 'company_account_street',
	COMPANY_ACCOUNT_VAT_NO: 'company_account_vatno',
	COMPANY_ACCOUNT_WEBSITE: 'company_account_website',
	COMPANY_ACCOUNT_ZIP: 'company_account_zip',
	CONFIRM_QUOTE_LINES: 'confirmQuoteLines',
	CONFIRM_SMART_INBOX_TASKS: 'confirm_smart_inbox_tasks',
	CONVERT_BILLABLE_HOURS_TO_INT: 'convert_billable_hours_to_int',
	CREATE_NEW_ENTRIES_IN_SALES_DOCS: 'create_new_entries_in_sales_docs',
	CSV_DELIMITER: 'csv_delimiter',
	CUSTOMER_PORTAL_DEFAULT_PDF_TEMPLATE_ID:
		'customer_portal_default_pdf_template_id',
	CUSTOMER_PORTAL_DONT_SHOW_DASHBOARD: 'customer_portal_dont_show_dashboard',
	DEFAULT_ACTIVITY_CONTACTS: 'default_activity_contacts',
	DEFAULT_ACTIVITY_CUSTOMER_SERVICE: 'default_activity_customService',
	DEFAULT_ACTIVITY_INVOICES: 'default_activity_invoices',
	DEFAULT_ACTIVITY_METERS: 'default_activity_meters',
	DEFAULT_ACTIVITY_ORDERS: 'default_activity_orders',
	DEFAULT_ACTIVITY_QUOTES: 'default_activity_quotes',
	DEFAULT_ADD_COMPANY_CATEGORY: 'default_add_company_category',
	DEFAULT_CLIENT_PROFILE: 'default_client_profile',
	DEFAULT_CURRENCY: 'default_currency',
	DEFAULT_ELION_PHONE: 'default_elion_phone',
	DEFAULT_EMAIL_BCC_CUSTOMER_SERVICE: 'default_email_bcc_customerService',
	DEFAULT_EMAIL_BCC_GENERAL: 'default_email_bcc_general',
	DEFAULT_EMAIL_BCC_INVOICES: 'default_email_bcc_invoices',
	DEFAULT_EMAIL_BCC_METERS: 'default_email_bcc_meters',
	DEFAULT_EMAIL_MESSAGE_CALENDAR_INVITES:
		'default_email_message_calendarInvites',
	DEFAULT_EMAIL_MESSAGE_INVOICES: 'default_email_message_invoices',
	DEFAULT_EMAIL_MESSAGE_METERS: 'default_email_message_meters',
	DEFAULT_EVENT_DURATION: 'default_event_duration',
	DEFAULT_INVOICE_DEADLINE: 'default_invoice_deadline',
	DEFAULT_INVOICE_PERIOD: 'default_invoice_period',
	DEFAULT_INVOICE_REMINDER_PROFILE: 'default_invoice_reminder_profile',
	DEFAULT_LOCALE_DATE: 'default_locale_date',
	LOCALE_DATE: 'locale_date',
	DEFAULT_LOCALE_NUMBER: 'default_locale_number',
	LOCALE_NUMBER: 'locale_number',
	DEFAULT_LOCALE_TIME: 'default_locale_time',
	LOCALE_CLOCK: 'locale_clock',
	DEFAULT_ORDER_DEADLINE: 'default_order_deadline',
	DEFAULT_PRICE_LIST: 'default_price_list',
	DEFAULT_PRIORITY: 'default_priority',
	DEFAULT_PURCHASES_VAT: 'default_purchases_vat',
	DEFAULT_QUOTE_DEADLINE: 'default_quote_deadline',
	DEFAULT_QUOTE_ESTIMATED_CLOSING_DATE:
		'default_quote_estimated_closing_date',
	DEFAULT_REAL_ESTATE_OUTPUTS: 'default_real_estate_outputs',
	DEFAULT_RECEIPT_ACCOUNT: 'default_receipt_account',
	DEFAULT_REPOSITORY: 'default_repository',
	DEFAULT_SALES_VAT: 'default_sales_vat',
	DEFAULT_SMTP: 'default_smtp',
	DEFAULT_SUBTASK_DURATION: 'default_subtask_duration',
	DEFAULT_TASK_DURATION: 'default_task_duration',
	DEFAULT_TASK_REPLY_FROM_EMAIL: 'default_task_reply_from_email',
	DIRECTO_SALESMAN: 'directo_salesman',
	DISCOUNTS_ON_LINES: 'discounts_on_lines',
	DISPLAY_CURRENCY: 'display_currency',
	DISPLAY_CURRENCY_MULTIPLIER: 'display_currency_multiplier',
	DISPLAY_DENSITY: 'display_density',
	DROPBOX_ACCESS_TOKEN: 'dropbox_access_token',
	DROPBOX_DELTA_CURSOR: 'dropbox_delta_cursor',
	DROPBOX_DELTA_TIME: 'dropbox_delta_time',
	DURATIONS: 'durations',
	DURATIONS_TYPE: 'durations_type',
	ELION_SMART_CALLS: 'elion_smart_calls',
	EMAIL_PREFERRED_CONTACT_INVOICES: 'email_preferred_contact_invoices',
	EMAIL_SENDER_ALIASES: 'email_sender_aliases',
	EMAIL_TIMEOUT_FOR_SCHEDULED_INVOICES:
		'email_timeout_for_scheduled_invoices',
	EMAIL_TO_TASK: 'email_to_task',
	ENABLE_MODEL_CHANGE_LOG: 'enableModelChangeLog',
	EVENTS_AUTO_SUGGEST_MODULES: 'events_auto_suggest_modules',
	EVENTS_VIEW_MODULES: 'events_view_modules',
	EXCHANGE_AUTH_NTLM_ONLY: 'exchange_auth_ntlm_only',
	EXCHANGE_ERROR_FIRST_TRY_FAILED: 'exchange_error_first_try_failed',
	EXCHANGE_ERROR_MAIL_SENT: 'exchange_error_mail_sent',
	EXCHANGE_HOST: 'exchange_host',
	EXCHANGE_LAST_SYNC_STATE: 'exchange_last_sync_state',
	EXCHANGE_LAST_SYNC_TIME: 'exchange_last_sync_time',
	EXCHANGE_PASSWORD: 'exchange_password',
	EXCHANGE_SYNC_RUNNING: 'exchange_sync_running',
	EXCHANGE_USERNAME: 'exchange_username',
	EXPENSES_RECEIPTS_FROM_DIRECTO: 'billsReceiptsFromDirecto',
	EXPENSES_TO_DIRECTO: 'billsToDirecto',
	FTP_DOMAIN: 'ftp_domain',
	FTP_PASSWORD: 'ftp_password',
	FTP_PROTOCOL: 'ftp_protocol',
	FTP_SERVER: 'ftp_server',
	FTP_SERVER_PORT: 'ftp_server_port',
	FTP_USERNAME: 'ftp_username',
	GLOBAL_FINE: 'global_fine',
	GL_TRANS_INCREMENTAL_NUMBER: 'gl_trans_incremental_number',
	GL_TRANS_LAST_RUN_DATE: 'gl_trans_last_run_date',
	GOOGLE_ANALYTICS_LAST_RUN_DATE: 'google_analytics_last_run_date',
	GOOGLE_SINGLE_SIGN_IN_ENABLED: 'google_single_sign_in_enabled',
	GROUP_SALES_DOCS_LINE_DATES_BY_MONTH:
		'group_sales_docs_line_dates_by_month',
	HANSA_ADDRESS_MAPPING: 'hansa_address_mapping',
	HANSA_BASE_CURRENCY_1: 'hansa_base_currency_1',
	HANSA_BASE_CURRENCY_2: 'hansa_base_currency_2',
	HANSA_BILLS_SERIES: 'hansa_bills_series',
	HANSA_CREDIT_INVOICES_WITH_PLUS_SIGN:
		'hansa_credit_invoices_with_plus_sign',
	HANSA_DESCRIPTION_FIELD_VALUE: 'hansa_description_field_value',
	HANSA_EXPORT_DATE_FORMAT: 'hansa_export_date_format',
	HANSA_HEADER_COMPANY_IDENTIFIER: 'hansa_header_company_identifier',
	HANSA_MULTIPLE_PRODUCTS_WITH_SAME_CODE:
		'hansa_multiple_products_with_same_code',
	HANSA_PREPAYMENTS_PREFIX: 'hansa_prepayments_prefix',
	HANSA_RECEIPTS_SERIES: 'hansa_receipts_series',
	HANSA_SALESMAN: 'hansa_salesman',
	HANSA_USE_FINANCE_OBJECTS_AS_ACCOUNTS:
		'hansa_use_finance_objects_as_accounts',
	HIDE_INVOICE_ISSUER_ID: 'hide_invoice_issuer_id',
	HIDE_MAIL_TASKBOX: 'hide_mail_taskbox',
	HIDE_OTHER_ACCOUNT_PRICE_LISTS: 'hide_other_account_price_lists',
	IGNORE_EARVEKESKUS_SYNC_DOMAIN_RESTRICTION:
		'ignore_earvekeskus_sync_domain_restriction',
	IGNORE_TELIA_SMART_CALLS_DOMAIN_RESTRICTION:
		'ignore_telia_smart_calls_domain_restriction',
	INCLUDE_WEEKENDS_WHEN_SCHEDULING: 'include_weekends_when_scheduling',
	INDIVIDUAL_TIME_ALLOCATION: 'individual_time_allocation',
	INDIVIDUAL_TIME_ALLOCATION_OPTION_DEFAULT: 'default',
	INDIVIDUAL_TIME_ALLOCATION_OPTION_INDIVIDUAL: 'individual',
	INLINE_VIDEOS_SEEN: 'inline_videos_seen',
	INTEGRATION_VISIBILITY_EXCEPTION: 'integration_visibility_exception',
	INVOICES_PDF_COMMENT_CONTENTS: 'invoices_pdf_comment_contents',
	INVOICES_PDF_TEMPLATE: 'invoices_pdf_template',
	INVOICES_TO_DIRECTO: 'invoicesToDirecto',
	INVOICE_DEADLINE_INPUT_TYPE: 'invoice_deadline_input_type',
	INVOICE_LINES_DATE_RANGE_IS_REQUIRED:
		'invoice_lines_date_range_is_required',
	INVOICE_PERIOD_ENABLED: 'invoice_period_enabled',
	LANGUAGE: 'language',
	LAST_AUTOMATIC_HANSA_EXPORT_DATE: 'lastAutomaticHansaExportDate',
	LINE_BASED_VAT: 'line_based_vat',
	LOCKING_INVOICES_DATE: 'locking_invoices_date',
	LOCKING_INVOICES_ENABLED: 'locking_invoices_enabled',
	LOGIN_IP_LIMIT: 'login_ip_limit',
	MAILCHIMP_LAST_CACHE: 'mailchimp_last_cache',
	MAILCHIMP_LAST_SYNC: 'mailchimp_last_sync',
	MAILCHIMP_MERGE_VARS: 'mailchimp_merge_vars',
	MAILCHIMP_TOKEN: 'mailchimp_token',
	METERS_LAST_DATE_OF_INVOICING: 'meters_last_date_of_invoicing',
	METERS_NOTIFICATION_INTERVALL: 'meters_notification_intervall',
	MONTHLY_INVOICE_AMOUNT_TYPE: 'monthly_invoice_amount_type',
	MOVE_FILES_TO_DROPBOX: 'move_files_to_dropbox',
	MOVE_FILES_TO_GOOGLE_DRIVE: 'move_files_to_google_drive',
	MULTI_FACTOR_AUTHENTICATION_ENABLED: 'multi_factor_authentication_enabled',
	MULTI_FACTOR_AUTHENTICATION_EXPIRE_TYPE:
		'multi_factor_authentication_expire_type',
	NEW_BASE_CURRENCY: 'new_base_currency',
	NEW_CURRENCIES_BEING_POPULATED: 'new_currencies_being_populated',
	NEW_CURRENCIES_BEING_POPULATED_DATES:
		'new_currencies_being_populated_dates',
	NEW_PERSON_IS_CLIENT_BY_DEFAULT: 'new_person_is_client_by_default',
	NOTIFICATIONS_LAST_CHECKED_DATE: 'notifications_last_checked_date',
	NO_DISCOUNTS_TO_NEW_RENT_ORDER_PRODUCTS:
		'no_discounts_to_new_rent_order_products',
	NUMBERS_AFTER_COMMA: 'numbers_after_comma',
	NUMBER_OF_INVOICELINES: 'number_of_invoicelines',
	NUMBER_OF_ORDERLINES: 'number_of_orderlines',
	NUMBER_OF_PURCHASES_LINES: 'number_of_purchases_lines',
	NUMBER_OF_QUOTELINES: 'number_of_quotelines',
	ORDERS_PDF_COMMENT_CONTENTS: 'orders_pdf_comment_contents',
	ORDERS_PDF_TEMPLATE: 'orders_pdf_template',
	ORDER_LINES_DATE_RANGE_IS_REQUIRED: 'order_lines_date_range_is_required',
	PAAROT_DOCS_NUMBERING: 'paarot_docs_numbering',
	PDF_DEFAULT_IMPERIAL_UNITS: 'pdf_default_imperial_units',
	PREPAYMENTS_PDF_TEMPLATE: 'prepayments_pdf_template',
	PREVIOUS_CURRENCIES: 'previous_currencies',
	PRODUCT_CODE: 'code',
	PRODUCT_FIELDS: 'product_fields',
	PRODUCT_GROUPS: 'groups',
	PRODUCT_LINKED_PRODUCTS: 'linkedProducts',
	PRODUCT_PACKAGES: 'packages',
	PRODUCT_PAGE_OCCUPANCY_COEFFICIENT: 'page_occupancy_coefficient',
	PRODUCT_PICTURES: 'pictures',
	PRODUCT_SYMBOL: 'symbol',
	PROJECT_TABS_ENABLED: 'project_tabs_enabled',
	PROJECTS_TO_DIRECTO: 'projectsToDirecto',
	PURCHASE_ORDERS_PDF_COMMENT_CONTENTS: 'purchaseOrders_pdf_comment_contents',
	QUICKBOOKS_LAST_UPDATE: 'quickbooks_last_update',
	QUICKBOOKS_REALM_ID: 'quickbooks_realmId',
	QUICKBOOKS_TOKENS: 'quickbooks_tokens',
	QUOTA_LIMIT_SYSTEM_MAIL_SENT: 'quota_limit_system_mail_sent',
	QUOTES_PDF_COMMENT_CONTENTS: 'quotes_pdf_comment_contents',
	QUOTES_PDF_TEMPLATE: 'quotes_pdf_template',
	QUOTE_LINES_DATE_RANGE_IS_REQUIRED: 'quote_lines_date_range_is_required',
	READINGS_INVOICES_GENERATION: 'readings_invoices_generation',
	READINGS_PREPAYMENT_ALLOCATION: 'readings_prepayment_allocation',
	REAL_ESTATE_ADDITIONAL_TYPES: 'realestate_additional_types',
	REAL_ESTATE_OBJECT_TYPES: 'real_estate_object_types',
	REAL_ESTATE_OUTPUTS_ARRAY: 'real_estate_outputs_array',
	REAL_ESTATE_PICTURES_REPOSITORY: 'real_estate_pictures_repository',
	REAL_ESTATE_SELECTED_COUNTRIES: 'realestate_selected_countries',
	REAL_ESTATE_SELECTED_LANGUAGES: 'realestate_selected_languages',
	REAL_ESTATE_SELECTED_PROPERTY_TYPES: 'real_estate_selected_property_types',
	REAL_ESTATE_TRANSACTION_TYPES: 'real_estate_transaction_types',
	REAL_ESTATE_WATERMARK_SETTINGS: 'real_estate_watermark_settings',
	RECEIPTS_FROM_DIRECTO: 'receiptsFromDirecto',
	REFERRAL_LINK: 'referral_link',
	REGENERATE_CURRENCIES_DATA: 'regenerate_currencies_data',
	REGENERATE_CURRENCY_RATES: 'regenerate_currency_rates',
	REMOVE_TOGGLE_FOR_QUOTE_LINK_IN_EMAIL:
		'remove_toggle_for_quote_link_in_email',
	RENTAL_ORDERS_PDF_COMMENT_CONTENTS: 'rentalOrders_pdf_comment_contents',
	RENT_ORDERS_DEFAULT_ISSUE_TIME_H: 'rent_orders_default_issue_time_h',
	RENT_ORDERS_DEFAULT_MANAGER: 'rent_orders_default_manager',
	RENT_ORDERS_DEFAULT_RECEIVE_DATE: 'rent_orders_default_receive_date',
	RENT_ORDERS_DEFAULT_RECEIVE_TIME_H: 'rent_orders_default_receive_time_h',
	RENT_ORDERS_LIST_ORDER: 'rent_orders_list_order',
	RENT_ORDERS_PDF_TEMPLATE: 'rentalOrders_pdf_template',
	RENT_ORDERS_STOCK_ACCOUNTING_ACCORDING_TO_STATUS_COMPLETED:
		'rent_orders_stock_status_completed',
	ROUND_VAT_ON_LINES: 'round_vat_on_lines',
	SEARCH_MODULES: 'search_modules',
	SESSION_EXPIRES_IN_MINUTES: 'sessionExpiresInMinutes',
	SESSION_EXPIRE_ENABLED: 'session_expire_enabled',
	SETUP_STEP: 'setupStep',
	SEWER_CONSUMPTION_RELATED_PRODUCT: 'sewer_consumption_related_product',
	SHOULD_SHOW_GENERAL_SEARCH_TOUR: 'should_show_general_search_tour',
	SHOW_DESCRIPTION_IN_CAL_INVITE: 'show_description_in_calendar_invite',
	SHOW_EVENT_PRICE: 'show_event_price',
	SHOW_INVOICE_LINES_DATES: 'show_invoice_lines_dates',
	SHOW_ORDER_LINES_DATES: 'show_order_lines_dates',
	SHOW_ORDER_PROJECT_NUMBER: 'show_order_project_number',
	SHOW_PROJECT_GANTT_BY_DEFAULT: 'show_project_gantt_by_default',
	SHOW_PROJECT_TIMELINE_BY_DEFAULT: 'show_project_timeline_by_default',
	SHOW_PURCHASES_PROJECT_NUMBER: 'show_purchases_project_number',
	SHOW_QUOTED_VS_ACTUAL_BY_DEFAULT: 'show_quoted_vs_actual_by_default',
	SHOW_QUOTE_LINES_DATES: 'show_quote_lines_dates',
	SHOW_QUOTE_PROJECT_NUMBER: 'show_quote_project_number',
	SHOW_SHIPMENT_DATE: 'show_shipment_date',
	SHOW_QUOTE_COMMENT_IN_RETROSPECTIVE_LINKING:
		'show_quote_comment_in_retrospective_linking',
	SINGLE_SIGN_ON_IDENTITY_PROVIDER_ENTITY_ID:
		'single_sign_on_identity_provider_entity_id',
	SINGLE_SIGN_ON_IDENTITY_PROVIDER_URL:
		'single_sign_on_identity_provider_url',
	SINGLE_SIGN_ON_IDENTITY_PROVIDER_X509CERT:
		'single_sign_on_identity_provider_x509cert',
	SINGLE_SIGN_ON_SETTINGS_VERIFIED: 'single_sign_on_settings_verified',
	SINGLE_SIGN_ON_ENABLED: 'single_sign_on_enabled',
	SINGLE_SIGN_ON_REGULAR_LOGIN_ENABLED:
		'single_sign_on_regular_login_enabled',
	SITE_LOGO_PATH: 'site_logo_path',
	SITE_PACKAGE_CHANGE_STATE: 'site_package_change_state',
	SITE_PRINT_LOGO_PATH: 'site_print_logo_path',
	SMART_INBOX_EMAIL_ORDERING: 'smart_inbox_email_ordering',
	STICKY_ISSUER_ID: 'sticky_issuer_id',
	SUB_CALCULATION_MONEY_NUMBERS_AFTER_COMMA:
		'sub_calculation_money_numbers_after_comma',
	SYSTEM_DAY_UNIT_SUB_VALUE: 'system_day_unit_sub_value',
	TIME_BILLING_UNSPECIFIED_ACTIVITY_PRODUCT_ID:
		'time_billing_unspecified_activity_product_id',
	TRIAL_ACCESS_HASH: 'trial_access_hash',
	USER_PASSWORD_EXPIRES: 'user_password_expires',
	USE_ACTIVITY_LOCKING: 'use_activity_locking',
	USE_ACTIVITY_POINTS: 'use_activity_points',
	USE_ADDITIONAL_COMMENT_ON_LINES: 'use_additional_comment_on_lines',
	USE_ADDITIONAL_DISCOUNTS: 'use_additional_discounts',
	USE_AMOUNT2: 'use_amount2',
	USE_BILLABLE_HOURS: 'use_billable_hours',
	USE_BROADCASTING_SCHEDULE: 'use_broadcasting_schedule',
	USE_BROKER_FEE_AGREEMENT: 'use_broker_fee_agreement',
	USE_CONFIRM_EVENTS: 'use_confirm_events',
	USE_DEADLINE_WITH_TIME: 'use_deadline_with_time',
	USE_DEPOTS: 'use_depots',
	USE_FINANCE_ACCOUNTS: 'use_finance_accounts',
	USE_FINANCE_OBJECTS: 'use_finance_objects',
	USE_FINANCE_OBJECTS_FOR_QUOTES: 'use_finance_objects_for_quotes',
	USE_INTERESTED_PARTY: 'use_interested_party',
	USE_MARGIN: 'use_margin',
	USE_MARKUP: 'use_markup',
	USE_MODAL_PRODUCT_SELECTION: 'use_modal_product_selection',
	USE_NOTIFICATIONS_IN_APP: 'use_notifications_in_app',
	USE_NO_MARGIN_AMOUNT_CALCULATION_IN_PAAROT:
		'use_no_margin_amount_calculation_in_paarot',
	USE_ONLY_HANSA_RECEIPTS: 'use_only_hansa_receipts',
	USE_PERSONAL_PROJECTS: 'use_personal_projects',
	USE_PROJECT_BASED_PRODUCT_PRICES: 'use_project_based_product_prices',
	USE_PROJECT_NAME_IN_EMAIL_SUBJECT: 'use_project_name_in_email_subject',
	USE_PROJECT_RELATED_REAL_ESTATE_OBJECTS:
		'use_project_related_re_estate_objects',
	USE_REAL_ESTATE_NOTIFICATIONS: 'use_realestate_notifications',
	USE_RECOGNITION_DATE: 'use_recognition_date',
	USE_REFERENCE_NUMBER: 'use_reference_number',
	USE_REIMBURSABLE_EXPENSES: 'use_reimbursable_expenses',
	USE_ROLES: 'use_roles',
	ASSIGN_ROLES_TO_TASKS: 'assign_roles_to_tasks',
	USE_SERVICE_BASED_PRICING: 'use_service_based_pricing',
	USE_SUBTASKS: 'use_subtasks',
	USE_SUPPLIERS_LIST: 'use_suppliers_list',
	USE_TASK_OWNER: 'use_task_owner',
	USE_TASK_STATUSES: 'use_task_statuses',
	USE_TASK_TO_MULTIPLE_USERS: 'use_task_to_multiple_users',
	USE_TRACKER_FOR_BILLABLE_TIME: 'use_tracker_for_billable_time',
	USE_USERS_ON_SALES_DOCUMENTS_LINES: 'use_users_on_sales_documents_lines',
	USE_YESTERDAY_CURRENCY_RATE: 'use_yesterday_currency_rate',
	USE_HELP_STATION_TRIAL_GUIDE: 'use_help_station_trial_guide',
	WATER_CONSUMPTION_RELATED_PRODUCT: 'water_consumption_related_product',
	WEEK_STARTS_ON: 'week_starts_on',
	XERO_LAST_UPDATE: 'xero_last_update',
	XERO_TOKENS: 'xero_tokens',
	ZOOM_LEVEL_GANTT_CHART: 'zoom_level_gantt',
	WATERMARK_PATH: 'watermark_path',
	GLOBAL_SEARCH_USE_PARTIAL_MATCHES: 'global_search_use_partial_matches',
	DISABLE_ELI_CHAT: 'disable_eli_chat',
	ENABLE_ELI_INSIGHTS: 'enable_eli_insights',
	TIMESHEET_REMINDERS: 'timesheet_reminders',
	SHOW_TASK_BUDGET_CHART: 'show_task_budget_chart',
	DISABLE_ELI_PROJECT_HEALTH: 'disable_eli_project_health',
	DISABLE_ELI_FEATURES: 'disable_eli_features',
	DISABLE_GENERAL_ELI_CHAT: 'disable_general_eli_chat',
	MCP_SERVER_ENABLED: 'mcp_server_enabled',
} as const;
